import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  api,
  unwrap,
  type FilenameProfile,
  type FilenameProfileInput,
  type ProfileMatch,
} from "../api/client";
import { ProfileGenerator } from "./ProfileGenerator";

const PROFILES_KEY = ["profiles"] as const;

const BLANK: FilenameProfileInput = {
  device: "",
  type: "",
  pattern: "",
  ts_format: "%Y-%m-%d_%H-%M-%S",
  utc_offset_minutes: 0,
  source_template: "",
  pps_aligned: false,
  ts_is_session_start: false,
  priority: 100,
  enabled: true,
};

function inputOf(p: FilenameProfile): FilenameProfileInput {
  return {
    device: p.device,
    type: p.type,
    pattern: p.pattern,
    ts_format: p.ts_format,
    utc_offset_minutes: p.utc_offset_minutes,
    source_template: p.source_template ?? "",
    pps_aligned: p.pps_aligned,
    ts_is_session_start: p.ts_is_session_start,
    priority: p.priority,
    enabled: p.enabled,
  };
}

function clock(minutes: number): string {
  if (minutes === 0) return "UTC";
  const sign = minutes > 0 ? "+" : "−";
  const m = Math.abs(minutes);
  return `UTC${sign}${Math.floor(m / 60)}${m % 60 ? `:${String(m % 60).padStart(2, "0")}` : ""}`;
}

export function ProfilesPage() {
  const qc = useQueryClient();
  const profiles = useQuery({
    queryKey: PROFILES_KEY,
    queryFn: () => unwrap(api.GET("/api/profiles")),
  });
  const [editing, setEditing] = useState<{
    id: string | null;
    draft: FilenameProfileInput;
  } | null>(null);
  const [samples, setSamples] = useState("");
  const [results, setResults] = useState<ProfileMatch[] | null>(null);

  const refresh = () => void qc.invalidateQueries({ queryKey: PROFILES_KEY });
  const save = useMutation({
    mutationFn: ({
      id,
      draft,
    }: {
      id: string | null;
      draft: FilenameProfileInput;
    }) =>
      id
        ? unwrap(
            api.PUT("/api/profiles/{id}", {
              params: { path: { id } },
              body: draft,
            }),
          )
        : unwrap(api.POST("/api/profiles", { body: draft })),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE("/api/profiles/{id}", { params: { path: { id } } })),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const test = useMutation({
    mutationFn: (profile: FilenameProfileInput | null) =>
      unwrap(
        api.POST("/api/profiles/test", {
          body: {
            profile,
            filenames: samples
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean),
          },
        }),
      ),
    onSuccess: setResults,
  });
  const recheck = useMutation({
    mutationFn: () => unwrap(api.POST("/api/imports/recheck")),
  });

  // Grouped by device for reading; matching still tries every enabled profile in priority order.
  const groups = Object.entries(
    (profiles.data ?? []).reduce<Record<string, FilenameProfile[]>>(
      (acc, p) => {
        (acc[p.device] ??= []).push(p);
        return acc;
      },
      {},
    ),
  ).sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  const devices = groups.map(([d]) => d).filter(Boolean);

  const set = (change: Partial<FilenameProfileInput>) =>
    setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...change } } : e));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (editing) save.mutate(editing);
  };

  return (
    <Stack spacing={3} component="section">
      <Typography variant="h5" component="h2">
        Filename profiles
      </Typography>
      <Typography color="text.secondary">
        How each device type's file names give the recording's source and start
        time. A device needs one profile per way it names its
        files. Profiles are tried in priority order across all devices; the
        first that matches is used. Files that match none need review.
      </Typography>
      {profiles.isError && (
        <Alert severity="error">{profiles.error.message}</Alert>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell>Priority</TableCell>
                <TableCell>Data type</TableCell>
                <TableCell>Pattern</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Clock</TableCell>
                <TableCell>1PPS</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(([device, rows]) => (
                <Fragment key={device}>
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      sx={{ bgcolor: "action.hover", fontWeight: 500 }}
                    >
                      {device || "No device"}
                    </TableCell>
                  </TableRow>
                  {rows.map((p) => (
                    <TableRow key={p.id} selected={editing?.id === p.id}>
                      <TableCell>{p.priority}</TableCell>
                      <TableCell>
                        <Stack
                          direction="row"
                          sx={{ gap: 1, alignItems: "center" }}
                        >
                          {p.type}
                          {!p.enabled && <Chip size="small" label="Disabled" />}
                        </Stack>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          wordBreak: "break-all",
                        }}
                      >
                        {p.pattern}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {p.source_template ?? "From the content"}
                      </TableCell>
                      <TableCell>{clock(p.utc_offset_minutes)}</TableCell>
                      <TableCell>{p.pps_aligned ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() =>
                            setEditing({ id: p.id, draft: inputOf(p) })
                          }
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Stack
        direction="row"
        sx={{ gap: 2, flexWrap: "wrap", alignItems: "center" }}
      >
        <Button
          variant="contained"
          onClick={() => setEditing({ id: null, draft: BLANK })}
        >
          New profile
        </Button>
        <Button
          variant="outlined"
          disabled={recheck.isPending}
          onClick={() => recheck.mutate()}
        >
          Re-check files needing review
        </Button>
        {recheck.isSuccess && (
          <Typography variant="body2">
            Checked {recheck.data.checked}: {recheck.data.catalogued} now
            catalogued, {recheck.data.still_needs_review} still need review.
          </Typography>
        )}
        {recheck.isError && (
          <Alert severity="error">{recheck.error.message}</Alert>
        )}
      </Stack>

      <ProfileGenerator
        onUse={(draft, examples) => {
          setEditing({ id: null, draft });
          setSamples(examples.join("\n"));
          setResults(null);
        }}
      />

      {editing && (
        <Paper
          variant="outlined"
          sx={{ p: 2 }}
          component="form"
          onSubmit={submit}
        >
          <Stack spacing={2}>
            <Typography variant="h6" component="h3">
              {editing.id ? `Edit ${editing.draft.device} ${editing.draft.type}` : "New profile"}
            </Typography>
            {save.isError && (
              <Alert severity="error">{save.error.message}</Alert>
            )}
            {remove.isError && (
              <Alert severity="error">{remove.error.message}</Alert>
            )}
            <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
              <Autocomplete
                freeSolo
                options={devices}
                value={editing.draft.device}
                onInputChange={(_, device) => set({ device })}
                sx={{ minWidth: 220 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Device"
                    helperText="One device may need several profiles"
                  />
                )}
              />
              <TextField
                label="Data type"
                required
                value={editing.draft.type}
                onChange={(e) => set({ type: e.target.value })}
                helperText="e.g. Video; unique within the device"
              />
              <TextField
                label="Priority"
                type="number"
                value={editing.draft.priority}
                onChange={(e) => set({ priority: Number(e.target.value) })}
                helperText="Lower is tried first"
              />
              <TextField
                label="Clock offset from UTC (minutes)"
                type="number"
                value={editing.draft.utc_offset_minutes}
                onChange={(e) =>
                  set({ utc_offset_minutes: Number(e.target.value) })
                }
                helperText="0 if the file names are in UTC"
              />
            </Stack>
            <TextField
              label="Pattern"
              required
              value={editing.draft.pattern}
              onChange={(e) => set({ pattern: e.target.value })}
              slotProps={{
                htmlInput: {
                  style: { fontFamily: "monospace" },
                  spellCheck: false,
                },
              }}
              helperText="Regular expression with named groups: ts (required), ms, tz (the name's own UTC offset, e.g. +0100), seq, unit (if the name says which unit), and any used in the source"
            />
            <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Time format"
                required
                value={editing.draft.ts_format}
                onChange={(e) => set({ ts_format: e.target.value })}
                slotProps={{
                  htmlInput: { style: { fontFamily: "monospace" } },
                }}
                helperText="For the ts group, e.g. %Y-%m-%d_%H-%M-%S"
              />
              <TextField
                label="Source"
                value={editing.draft.source_template ?? ""}
                onChange={(e) => set({ source_template: e.target.value })}
                slotProps={{
                  htmlInput: { style: { fontFamily: "monospace" } },
                }}
                helperText="e.g. {unit}/video{channel}. {unit}: the name's unit group, else the folder. {unit|creator}: else the device named in the file. Empty: from the file"
              />
            </Stack>
            <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editing.draft.pps_aligned}
                    onChange={(e) => set({ pps_aligned: e.target.checked })}
                  />
                }
                label="Starts on a 1PPS edge"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editing.draft.ts_is_session_start}
                    onChange={(e) =>
                      set({ ts_is_session_start: e.target.checked })
                    }
                  />
                }
                label="Time is the session start, shared by all its files"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editing.draft.enabled}
                    onChange={(e) => set({ enabled: e.target.checked })}
                  />
                }
                label="Enabled"
              />
            </Stack>

            <TextField
              label="Example file names, one per line"
              multiline
              minRows={3}
              value={samples}
              onChange={(e) => setSamples(e.target.value)}
              slotProps={{
                htmlInput: {
                  style: { fontFamily: "monospace" },
                  spellCheck: false,
                },
              }}
            />
            <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
              <Button
                onClick={() => test.mutate(editing.draft)}
                disabled={!samples.trim() || test.isPending}
              >
                Test this profile
              </Button>
              <Button
                onClick={() => test.mutate(null)}
                disabled={!samples.trim() || test.isPending}
              >
                Test saved profiles
              </Button>
            </Stack>
            {test.isError && (
              <Alert severity="error">{test.error.message}</Alert>
            )}
            {results && <Results results={results} />}

            <Stack direction="row" sx={{ gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={save.isPending}
              >
                Save
              </Button>
              <Button onClick={() => setEditing(null)}>Cancel</Button>
              {editing.id && (
                <Button
                  color="error"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(editing.id ?? "")}
                >
                  Delete
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

function Results({ results }: { results: ProfileMatch[] }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>File name</TableCell>
            <TableCell>Profile</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Start (UTC)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((r, i) => (
            <TableRow key={`${r.filename}:${i}`}>
              <TableCell sx={{ fontFamily: "monospace" }}>
                {r.filename}
              </TableCell>
              <TableCell>
                {r.profile ?? (
                  <Chip size="small" color="warning" label="No match" />
                )}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>
                {r.source ?? "—"}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>
                {r.start_utc
                  ? r.start_utc.replace("T", " ").replace(/Z$/, "")
                  : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
